import React from "react";
import { PAIN_POINTS } from "@/lib/site";
import { HelpCircle, ArrowRight } from "lucide-react";

export default function PainPoint() {
    return (
        <section
            id="pain-point"
            data-testid="painpoint-section"
            className="py-20 md:py-28 bg-white"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-3xl">
                    <div className="font-mono-label text-[color:var(--maroon)]">
                        Common Challenges in Qualitative Research
                    </div>
                    <h2
                        data-testid="painpoint-heading"
                        className="mt-3 font-serif text-4xl sm:text-5xl text-[color:var(--navy)] tracking-tight leading-tight"
                        style={{ fontWeight: 500 }}
                    >
                        Do You Feel Lost in Qualitative Research?
                    </h2>
                    <div className="hr-gold mt-5" />
                    <p className="mt-5 text-slate-600 leading-relaxed max-w-2xl">
                        Many researchers are interested in qualitative research, but they often feel
                        unsure about how to begin, how to design the study, how to collect data, how
                        to analyse transcripts and how to write convincing findings.
                    </p>
                </div>

                <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {PAIN_POINTS.map((p, i) => (
                        <div
                            key={i}
                            data-testid={`painpoint-card-${i + 1}`}
                            className="group relative rounded-2xl border border-[color:var(--line)] bg-[color:var(--paper-tint)] p-6 hover:border-[color:var(--maroon)] hover:-translate-y-0.5 transition-all"
                        >
                            <div className="flex items-start gap-4">
                                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white border border-[color:var(--line)] text-[color:var(--maroon)] group-hover:bg-[color:var(--maroon)] group-hover:text-white transition-colors">
                                    <HelpCircle className="h-4 w-4" />
                                </span>
                                <div>
                                    <div className="font-mono-label text-slate-500">
                                        Concern {String(i + 1).padStart(2, "0")}
                                    </div>
                                    <p className="mt-1 font-serif text-lg text-[color:var(--navy)] leading-snug">
                                        “{p}”
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-10 rounded-3xl bg-[color:var(--navy)] text-white p-8 sm:p-10 flex flex-col md:flex-row md:items-center gap-6 relative overflow-hidden">
                    <div
                        className="absolute inset-0 pointer-events-none opacity-70"
                        style={{
                            backgroundImage:
                                "radial-gradient(circle at 20% 20%, rgba(181,138,43,0.16), transparent 45%), radial-gradient(circle at 85% 85%, rgba(122,31,47,0.28), transparent 45%)",
                        }}
                    />
                    <div className="relative flex-1">
                        <div className="font-mono-label text-[color:var(--gold-soft)]">
                            The Pathway
                        </div>
                        <p className="mt-2 font-serif text-2xl sm:text-3xl leading-snug" style={{ fontWeight: 500 }}>
                            This series gives you a{" "}
                            <span className="italic text-[color:var(--gold-soft)]">
                                clear, practical pathway
                            </span>
                            {" "}from the first idea to the published article.
                        </p>
                    </div>
                    <a
                        href="#journey"
                        data-testid="painpoint-journey-link"
                        className="relative inline-flex items-center gap-2 rounded-full bg-[color:var(--gold)] text-[color:var(--navy)] px-6 py-3 text-sm font-semibold hover:bg-[color:var(--gold-soft)] transition-colors shrink-0"
                    >
                        See the journey
                        <ArrowRight className="h-4 w-4" />
                    </a>
                </div>
            </div>
        </section>
    );
}
